<template>
  <component
    :is="expandable ? ExpandableProductTileFi : StaticProductTileFi"
    v-if="product"
    class="product-tile-fi"
    :product
    :href="productHref"
    :product-in-cart
    :is-shop
    :show-without-price
    :current-best-price
    :product-price
    :is-adding-item-to-cart
    :loading
    :best-price-loading
    :best-price
    :out-of-stock
    @go-to-product="goToProduct"
    @add-product="addProduct"
    @update-best-price="updateBestPrice"
    @quantity-input="onQuantityInput"
  />
</template>

<script setup lang="ts">
import { useQuery } from '@vue/apollo-composable';
import { computed, onMounted, ref } from 'vue';

import { useEventHub } from '@/composables/eventHub';
import { useFischer } from '@/composables/fischerPlugin';
import { usePriceCurrency } from '@/composables/priceCurrency';
import { useShoppingcart } from '@/composables/shoppingcart';
import { useTracking } from '@/composables/tracking';
import { useUser } from '@/composables/user';
import type {
  GetBestPriceQuery,
  GetBestPriceQueryVariables,
  HybrisFischerBestPrice,
} from '@/generated/graphql';
import DUMMY from '@/graphql/dummy.gql';
import { useNotificationStore } from '@/store/pinia/notification';

import ExpandableProductTileFi from './expandable-product-tile-fi/ExpandableProductTileFi.vue';
import StaticProductTileFi from './static-product-tile-fi/StaticProductTileFi.vue';

const importGETBESTPRICE = () => import('@/graphql/getBestPrice.gql');

export type AddProductData = { productID: string; productRequestedQuantity: number };
export type TileProduct = {
  id: string;
  name: string;
  Slug: string;
  image?: string;
  price?: {
    priceValue: number;
    priceCurrency: string;
  };
};

defineOptions({
  name: 'ProductTileFi',
});

const props = withDefaults(
  defineProps<{
    product: null | undefined | TileProduct;
    outOfStock?: boolean;
    bestPrice?: HybrisFischerBestPrice | null;
    bestPriceLoading?: boolean;
    expandable?: boolean;
    isRecommendation?: boolean;
    isGuidedRecommandation?: boolean;
  }>(),
  {
    bestPrice: undefined,
    bestPriceLoading: true,
  },
);

const emit = defineEmits<{
  'product-added': [value: AddProductData];
  'tile-clicked': [value: void];
}>();

const { formatPrice } = usePriceCurrency();
const fischer = useFischer();
const { logoutIfTokenInvalid } = useUser();
const { openGeneralErrorNotification } = useNotificationStore();
const { sendEvent } = useEventHub();
const { trackAddToCart } = useTracking();

const enterErrorState = (...error: any) => {
  logoutIfTokenInvalid(error?.[0]);
  console.error(...error);
  openGeneralErrorNotification();
};
const { cart, getCart, isAddingItemToCart, addProductToCart } = useShoppingcart({
  enterErrorState,
  logoutIfTokenInvalid,
  trackAddToCart,
});

const loading = ref<boolean>(false);
const newBestPrice = ref<HybrisFischerBestPrice | null>(null);

const isShop = computed<boolean>(
  () => (fischer.shop.isClosedShop() || fischer.shop.isOnlineShop()) && !fischer.app.isEditMode(),
);

const showWithoutPrice = computed<boolean>(() => fischer.shop.hasPurchasePermissionWithoutPrices());
const productInCart = computed<boolean>(
  () => cart.value?.products?.some((product) => product?.articleNo === props.product?.id) || false,
);

const currentBestPrice = computed<HybrisFischerBestPrice | undefined>(
  () => newBestPrice.value || props.bestPrice || undefined,
);

const productHref = computed<string | undefined>(
  () => props.product?.Slug && fischer.pages.products().url + props.product.Slug,
);

const productPrice = computed<string | null>(() => {
  if (props.product?.price?.priceValue && props.product?.price?.priceCurrency) {
    return formatPrice(props.product.price.priceValue, props.product.price.priceCurrency);
  }

  return null;
});

onMounted(() => {
  if (fischer.cartId() && fischer.shop.isRetailConnectCart() && !cart.value?.products) {
    getCart(false, true);
  }
});

const addProduct = async (productRequestedQuantity: number) => {
  const productID = props.product?.id || ''; // note: this WILL be defined since we check product in the template
  const result = await addProductToCart({
    productID,
    productRequestedQuantity,
    disableTracking: props.isRecommendation || props.isGuidedRecommandation,
  });
  if (!result.success) {
    return;
  }
  emit('product-added', { productID, productRequestedQuantity });

  if (!props.isRecommendation && !props.isGuidedRecommandation && !fischer.app.isFite()) {
    return;
  }

  sendEvent(props.isRecommendation ? 'recommendation_cart' : 'guided_recommendation_cart', {
    url: window ? window.location.href : '',
    productId: productID,
    refererId: fischer.catalogId(),
    itemQuantity: productRequestedQuantity,
  });
};

const onQuantityInput = () => {
  loading.value = true;
};

const updateBestPriceVariables = ref<GetBestPriceQueryVariables>({
  baseSiteId: fischer.baseSiteId(),
  accessToken: fischer.user.accessToken(),
  productCode: '',
  qty: 0,
  language: fischer.siteLanguage(),
  userId: fischer.user.id(),
  division: fischer.user.division(),
  distributionChannel: fischer.user.distributionChannel(),
});
const updateBestPriceDocument = ref(DUMMY);
const updateBestPrice = async (qty: number) => {
  const productCode = props.product?.id;
  if (!productCode || updateBestPriceVariables.value.qty === qty) {
    return;
  }

  loading.value = true;
  if (updateBestPriceVariables.value.productCode === '') {
    updateBestPriceDocument.value = (await importGETBESTPRICE()).default;
  }

  updateBestPriceVariables.value.qty = qty;
  updateBestPriceVariables.value.productCode = productCode;
};
const { onResult: onGetBestPriceResult, onError: onGetBestPriceError } =
  useQuery<GetBestPriceQuery>(updateBestPriceDocument, updateBestPriceVariables, {
    enabled: computed<boolean>(() => updateBestPriceVariables.value.productCode !== ''),
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
  });
onGetBestPriceResult(({ data, error, loading: queryLoading }) => {
  try {
    if (queryLoading) {
      return;
    }
    if (error) {
      throw error;
    }
    if (!data.getBestPrice) {
      throw new Error('did not get best price');
    }
    newBestPrice.value = data.getBestPrice;
  } catch (error) {
    enterErrorState(error);
  } finally {
    loading.value = false;
  }
});
onGetBestPriceError(enterErrorState);

const goToProduct = (itemQuantity: number) => {
  emit('tile-clicked');

  if (!fischer.app.isFite() && (props.isRecommendation || props.isGuidedRecommandation)) {
    localStorage.setItem(
      'recommendationClick',
      JSON.stringify({
        refererId: fischer.catalogId(),
        itemQuantity,
        guided: props.isGuidedRecommandation,
      }),
    );
  }

  if (productHref.value) {
    window.location.href = productHref.value;
  }
};
</script>

<style scoped src="./product-tile-fi" />
